/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Plus, Trash2, Edit2, CreditCard } from 'lucide-react';
import { toast } from 'sonner';

const CURRENCIES = [
  { code: 'eur', label: 'EUR · Euro (€)' },
  { code: 'usd', label: 'USD · Dollar US ($)' },
  { code: 'gbp', label: 'GBP · Livre sterling (£)' },
  { code: 'pln', label: 'PLN · Złoty polonais (zł)' },
  { code: 'sek', label: 'SEK · Couronne suédoise (kr)' },
];

const empty = { name: '', public_key: '', secret_key: '', currency: 'eur' };

export default function StripeAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const r = await api.get('/stripe-accounts');
    setAccounts(r.data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (sa) => {
    setEditing(sa);
    setForm({ name: sa.name, public_key: sa.public_key || '', secret_key: '', currency: sa.currency || 'eur' });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name || !form.public_key) {
      toast.error('Nom et clé publique requis');
      return;
    }
    if (!editing && !form.secret_key) {
      toast.error('Clé secrète requise à la création');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/stripe-accounts/${editing.id}`, form);
        toast.success('Compte Stripe mis à jour');
      } else {
        await api.post('/stripe-accounts', form);
        toast.success('Compte Stripe créé');
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!confirm('Supprimer ce compte Stripe ? (impossible si des vitrines sont liées)')) return;
    try {
      await api.delete(`/stripe-accounts/${id}`);
      toast.success('Compte supprimé');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-7xl">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="overline mb-2">— Infrastructure</div>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Comptes Stripe</h1>
          <p className="text-zinc-600 mt-2">Chaque compte Stripe = un site vitrine. Vous pouvez en gérer plusieurs en parallèle.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} data-testid="admin-add-stripe-button" className="btn-primary rounded-md">
              <Plus className="h-4 w-4 mr-2" /> Ajouter un compte Stripe
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-display tracking-tight">
                {editing ? 'Modifier le compte Stripe' : 'Nouveau compte Stripe'}
              </DialogTitle>
              <DialogDescription className="text-zinc-500">
                Renseignez les clés API d'un compte Stripe. La clé secrète est stockée chiffrée.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-xs">NOM INTERNE</Label>
                <Input
                  data-testid="admin-stripe-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1.5 h-10 rounded-md"
                  placeholder="Stripe Principal · Compte A"
                />
              </div>
              <div>
                <Label className="text-xs">PUBLISHABLE KEY (pk_...)</Label>
                <Input
                  data-testid="admin-stripe-pk-input"
                  value={form.public_key}
                  onChange={(e) => setForm({ ...form, public_key: e.target.value })}
                  className="mt-1.5 h-10 rounded-md font-mono text-xs"
                  placeholder="pk_live_... ou pk_test_..."
                />
              </div>
              <div>
                <Label className="text-xs">SECRET KEY (sk_...)</Label>
                <Input
                  data-testid="admin-stripe-sk-input"
                  type="password"
                  value={form.secret_key}
                  onChange={(e) => setForm({ ...form, secret_key: e.target.value })}
                  className="mt-1.5 h-10 rounded-md font-mono text-xs"
                  placeholder={editing ? '••• laisser vide pour ne pas changer' : 'sk_live_... ou sk_test_...'}
                />
                <p className="text-xs text-zinc-500 mt-1">Stockée chiffrée (Fernet AES-128). Jamais retournée par l'API.</p>
              </div>
              <div>
                <Label className="text-xs">DEVISE D'ENCAISSEMENT</Label>
                <Select
                  value={form.currency || 'eur'}
                  onValueChange={(v) => setForm({ ...form, currency: v })}
                >
                  <SelectTrigger data-testid="admin-stripe-currency-select" className="mt-1.5 h-10 rounded-md">
                    <SelectValue placeholder="Choisir la devise" />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((c) => (
                      <SelectItem key={c.code} value={c.code} data-testid={`admin-stripe-currency-${c.code}`}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-zinc-500 mt-1">Tout le checkout et l'encaissement de ce compte Stripe utiliseront cette devise.</p>
              </div>
              <p className="text-xs text-zinc-500 rounded-md border border-dashed border-zinc-200 p-3" data-testid="admin-stripe-token-capture-note">
                L'enregistrement des cartes (tokens) se règle désormais <strong>par boutique</strong>, dans Boutiques → modifier → « Enregistrement des cartes ».
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-md" onClick={() => setOpen(false)}>Annuler</Button>
              <Button
                onClick={save}
                disabled={saving}
                data-testid="admin-save-stripe-button"
                className="btn-primary rounded-md"
              >
                {editing ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {accounts.length === 0 ? (
          <Card className="card-flat p-10 col-span-full text-center">
            <CreditCard className="h-10 w-10 mx-auto text-zinc-300 mb-3" />
            <h3 className="font-display font-bold mb-1">Aucun compte Stripe</h3>
            <p className="text-zinc-500 text-sm">Ajoutez votre premier compte pour commencer à encaisser.</p>
          </Card>
        ) : accounts.map((sa) => (
          <Card key={sa.id} className="card-flat p-6" data-testid={`stripe-account-card-${sa.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg font-bold tracking-tight mb-1 truncate">{sa.name}</div>
                <div className="text-xs text-zinc-500 font-mono">{sa.public_key_masked} · {sa.secret_key_masked}</div>
                <div className="mt-3 flex items-center gap-2 text-xs">
                  <span className="px-2 py-0.5 rounded-md border bg-zinc-50 border-zinc-200 font-mono uppercase">{sa.currency}</span>
                  {sa.public_key.startsWith('pk_live_') && (
                    <span className="px-2 py-0.5 rounded-md border bg-emerald-50 border-emerald-200 text-emerald-700 font-mono">LIVE</span>
                  )}
                  {sa.public_key.startsWith('pk_test_') && (
                    <span className="px-2 py-0.5 rounded-md border bg-amber-50 border-amber-200 text-amber-700 font-mono">TEST</span>
                  )}
                </div>
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" onClick={() => openEdit(sa)} className="rounded-md">
                  <Edit2 className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => remove(sa.id)}
                  data-testid={`admin-delete-stripe-${sa.id}`}
                  className="rounded-md text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
